import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AdminPermissions } from 'constants/admin-permissions';
import {
  ADMIN_CHECK_TYPE_MULTI_SELECT_NORMALIZE,
  ALL_TYPES_FILTER_NAME,
  ALL_TYPES_FILTER_VALUE,
  SUPPORT_CHECKS_FILTER_VALUE,
  buildCheckTypeFilterOptions,
  isTruthyDbBool,
} from 'helpers/check-type-filter-options';
import {
  calculatePricingMarginInBaseCurrency,
  derivePricesFromAnchor,
  formatMarginPctDisplay,
  formatNumericInputValue,
  formatPricingAmount,
  getAnchorPriceField,
  getAnchorPriceValue,
  getBaseCurrency,
  getDerivedPricesForRow,
  getDisplayInternalCost,
  getDisplayPriceForRow,
  getEffectivePricingField,
  maxDiscountPctFromFloor,
  mergeNumericDraftsIntoPendingEdits,
  normalizeNumericInputOnBlur,
  numericDraftKey,
  parseExchangeRate,
  parseNonNegativeNumber,
  parseNumericDraftValue,
  pendingEditsWithNumericDrafts,
  pricingFieldValuesEqual,
  pruneMatchingPendingEdits,
  resolveFloorPrice,
  resolveInternalCostAud,
} from 'helpers/pricing-margin';
import { isFixedPricingMode } from 'helpers/pricing-mode';
import { useAccessControl } from 'hooks/access-control';
import { useDocumentTitle } from 'hooks/document-title';
import AdminPage from 'layouts/AdminPage/AdminPage';
import { useSelector } from 'react-redux';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { Button, Toggle } from 'components/FormComponents';
import { FormMessage, MessageType } from 'components/FormComponents/FormMessage/FormMessage';
import { Table, useTableContext } from 'components/Table/Table';

import { selectAuthentication } from 'api/authentication-slice';
import {
  useGetCheckTypePricingQuery,
  useGetGlobalRatesQuery,
  useLazyGetCompanyChecksReportQuery,
  useUpdateCheckTypePricingMutation,
  useUpdateGlobalRatesMutation,
} from 'api/check-type-pricing-api';
import { useGetCheckGroupsQuery, useGetCheckTypesQuery } from 'api/check-types-api';

import { ActiveViewBanner } from '../../Reporting/components/SavedViews/FiltersChangedBanner';
import { ReportingSavedViewsTableSync } from '../../Reporting/components/SavedViews/ReportingSavedViewsIntegrations';
import { SavedViewsDropdown } from '../../Reporting/components/SavedViews/SavedViewsDropdown';
import { useSavedViews } from '../../Reporting/components/SavedViews/useSavedViews';

const COUNTRY_OPTIONS = [
  { name: 'All', value: 'all' },
  { name: 'INT', value: 'INT' },
  { name: 'NZ', value: 'NZ' },
  { name: 'AU', value: 'AU' },
  { name: 'US', value: 'US' },
  { name: 'UK', value: 'UK' },
];

const HAS_SUBCHECKS_OPTIONS = [
  { name: 'All', value: 'all' },
  { name: 'Has Subchecks', value: 'yes' },
  { name: 'No Subchecks', value: 'no' },
];

const COMPANY_ACTIVE_OPTIONS = [
  { name: 'All Companies', value: 'all' },
  { name: 'Active', value: '1' },
  { name: 'Inactive', value: '0' },
];

function getTableQueryValues(query, key) {
  const v = query?.[key];
  const val = v != null && typeof v === 'object' && 'value' in v ? v.value : v;
  return Array.isArray(val) ? val : val != null && val !== '' ? [val] : [];
}

function adminListSourceSortKey(row) {
  const priceName = row?.price_name ?? '';
  if (priceName !== '' && priceName !== 'country.name') {
    return priceName;
  }
  return row?.source_display ?? '';
}

function buildCompanyChecksReportParams(query) {
  const params = {};
  const checkTypes = getTableQueryValues(query, 'check_type').filter(x => x !== 'all');
  const checkGroups = getTableQueryValues(query, 'check_group').filter(x => x !== 'all');
  const countries = getTableQueryValues(query, 'country').filter(x => x !== 'all');
  const companyActive = query?.company_active?.value ?? query?.company_active ?? 'all';
  const hasSubchecks = query?.has_subchecks?.value ?? query?.has_subchecks ?? 'all';

  if (checkTypes.length > 0) params.check_type = checkTypes.join(',');
  if (checkGroups.length > 0) params.check_group = checkGroups.join(',');
  if (countries.length > 0) params.country = countries.join(',');
  if (companyActive !== 'all') params.active = companyActive;
  if (hasSubchecks !== 'all') params.has_subchecks = hasSubchecks;

  return params;
}

const BASE_CURRENCY_OPTIONS = ['AUD', 'NZD', 'USD'];

const SUPPORT_PROVIDER_NOTES = 'support_provider_notes';
const FINANCE_PRICING_NOTES = 'finance_pricing_notes';

const NOTE_FIELDS = {
  [SUPPORT_PROVIDER_NOTES]: {
    column: 'Support notes',
    title: 'Support notes',
    placeholder: 'Which provider and product support should use...',
    addLabel: 'Add support notes',
    viewLabel: 'View support notes',
  },
  [FINANCE_PRICING_NOTES]: {
    column: 'Finance notes',
    title: 'Finance notes',
    placeholder: 'Pricing and provider selection notes...',
    addLabel: 'Add finance notes',
    viewLabel: 'View finance notes',
  },
};

const PricingNotesModal = ({ visible, row, field, notes, onClose, onSave, saving }) => {
  const copy = NOTE_FIELDS[field];
  const [draft, setDraft] = useState('');

  useEffect(() => {
    if (visible) {
      setDraft(notes ?? '');
    }
  }, [visible, notes]);

  const handleClose = async () => {
    const normalized = draft.trim() === '' ? null : draft;
    const existing = notes?.trim() ? notes : null;
    if (normalized !== existing) {
      await onSave(normalized);
      return;
    }
    onClose();
  };

  const rowLabel = [row?.title, row?.source_display !== '-' ? row?.source_display : null]
    .filter(Boolean)
    .join(' — ');

  return (
    <AnimatedModal
      visible={visible}
      onClose={handleClose}
      title={rowLabel ? `${copy.title} — ${rowLabel}` : copy.title}
      icon="edit">
      <textarea
        className="form-control"
        rows={8}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        disabled={saving}
        placeholder={copy.placeholder}
        aria-label={`${copy.title} for ${rowLabel || 'pricing row'}`}
      />
    </AnimatedModal>
  );
};

const ApplyFiltersButton = () => {
  const { manuallyApplyFilters } = useTableContext();
  return (
    <Button icon="filter" onClick={manuallyApplyFilters}>
      Apply Filters
    </Button>
  );
};

const PricingTableBody = ({
  tableRows,
  canEdit,
  showInputs,
  showCost,
  showMargin,
  pendingEdits,
  numericDrafts,
  setNumericDrafts,
  onPendingChange,
  onOpenNotes,
  saving,
  exchangeRate,
  internalCostManual,
  internalCostNonManual,
}) => {
  const { queryParams } = useTableContext();
  const searchTerm = (queryParams.search?.value ?? '').toLowerCase().trim();
  const filtered = !searchTerm
    ? tableRows
    : tableRows.filter(
        row =>
          (row.title ?? '').toLowerCase().includes(searchTerm) ||
          (row.type ?? '').toLowerCase().includes(searchTerm) ||
          (row.check_group_title ?? '').toLowerCase().includes(searchTerm) ||
          (row.charge_type_display ?? '').toLowerCase().includes(searchTerm) ||
          (row.source_display ?? '').toLowerCase().includes(searchTerm) ||
          (row.source_value ?? '').toLowerCase().includes(searchTerm) ||
          (row.price_name ?? '').toLowerCase().includes(searchTerm) ||
          (row.provider ?? '').toLowerCase().includes(searchTerm) ||
          (row.support_provider_notes ?? '').toLowerCase().includes(searchTerm) ||
          (row.finance_pricing_notes ?? '').toLowerCase().includes(searchTerm),
      );

  const getValue = (row, field) => {
    const v = getEffectivePricingField(row, pendingEdits, field);
    return v === null || v === undefined ? '' : String(v);
  };

  const getNumericInputValue = useCallback(
    (row, field) => {
      const key = numericDraftKey(row.id, field);
      if (key in numericDrafts) {
        return numericDrafts[key];
      }
      return formatNumericInputValue(getEffectivePricingField(row, pendingEdits, field));
    },
    [numericDrafts, pendingEdits],
  );

  const handleNumericInputChange = useCallback(
    (row, field, raw) => {
      setNumericDrafts(prev => ({ ...prev, [numericDraftKey(row.id, field)]: raw }));
    },
    [setNumericDrafts],
  );

  const handleNumericInputBlur = useCallback(
    (row, field, onCommit) => {
      const key = numericDraftKey(row.id, field);
      const raw = numericDrafts[key];
      setNumericDrafts(prev => {
        if (!(key in prev)) {
          return prev;
        }
        const next = { ...prev };
        delete next[key];
        return next;
      });
      if (raw === undefined) {
        return;
      }
      const value = parseNumericDraftValue(raw);
      if (value === undefined) {
        return;
      }
      onCommit(value);
    },
    [numericDrafts, setNumericDrafts],
  );

  const formatPrice = v =>
    v != null && v !== '' && !Number.isNaN(Number(v)) ? Number(v).toFixed(2) : '-';

  const handlePendingChange = useCallback(
    (row, field, e, { trim = false } = {}) => {
      const raw = e.target.value;
      const v = trim ? raw.trim() : raw;
      onPendingChange(row.id, field, v === '' ? null : v);
    },
    [onPendingChange],
  );

  const commitAnchorPrice = useCallback(
    (row, anchorValue) => {
      const baseCurrency = getBaseCurrency(row, pendingEdits);
      const derived = derivePricesFromAnchor(baseCurrency, anchorValue, exchangeRate);
      onPendingChange(row.id, {
        price_aud: derived.price_aud,
        price_nzd: derived.price_nzd,
        price_usd: derived.price_usd,
      });
    },
    [onPendingChange, pendingEdits, exchangeRate],
  );

  const commitProviderCost = useCallback(
    (row, value) => {
      onPendingChange(row.id, 'provider_cost', value);
    },
    [onPendingChange],
  );

  const commitFloor = useCallback(
    (row, value) => {
      onPendingChange(row.id, 'floor', value);
    },
    [onPendingChange],
  );

  const handleBaseCurrencyChange = useCallback(
    (row, e) => {
      const baseCurrency = e.target.value;
      const anchorField = getAnchorPriceField(baseCurrency);
      const anchorRaw = getEffectivePricingField(row, pendingEdits, anchorField);
      const anchorValue =
        anchorRaw != null && anchorRaw !== '' && !Number.isNaN(Number(anchorRaw))
          ? Number(anchorRaw)
          : null;
      const derived = derivePricesFromAnchor(baseCurrency, anchorValue, exchangeRate);
      onPendingChange(row.id, {
        base_currency: baseCurrency,
        price_aud: derived.price_aud,
        price_nzd: derived.price_nzd,
        price_usd: derived.price_usd,
      });
    },
    [onPendingChange, pendingEdits, exchangeRate],
  );

  const renderNumericInput = (row, field, { onCommit, ariaLabel }) => (
    <input
      type="text"
      inputMode="decimal"
      className="form-control form-control-sm"
      value={getNumericInputValue(row, field)}
      onChange={e => handleNumericInputChange(row, field, e.target.value)}
      onBlur={() => handleNumericInputBlur(row, field, onCommit)}
      disabled={saving}
      aria-label={ariaLabel}
    />
  );

  const renderMaxDiscountCell = row => {
    const edits = pendingEditsWithNumericDrafts(pendingEdits, numericDrafts, row, exchangeRate);
    return formatMarginPctDisplay(
      maxDiscountPctFromFloor(getAnchorPriceValue(row, edits), resolveFloorPrice(row, edits)),
    );
  };

  const renderPriceCell = (row, field) => {
    const baseCurrency = getBaseCurrency(row, pendingEdits);
    const anchorField = getAnchorPriceField(baseCurrency);
    const isAnchor = field === anchorField;

    if (showInputs && isAnchor) {
      return renderNumericInput(row, field, {
        onCommit: value => commitAnchorPrice(row, value),
        ariaLabel: `${field.replace('price_', '').toUpperCase()} for ${row.title}`,
      });
    }

    return formatPrice(
      getDisplayPriceForRow(
        row,
        field,
        pendingEditsWithNumericDrafts(pendingEdits, numericDrafts, row, exchangeRate),
        exchangeRate,
      ),
    );
  };

  const getSupportProviderNotes = row =>
    (getEffectivePricingField(row, pendingEdits, SUPPORT_PROVIDER_NOTES) ?? '').trim();

  const getProviderNoteLines = row => {
    const lines = [];
    const supportNotes = getSupportProviderNotes(row);
    if (supportNotes) {
      lines.push({ key: SUPPORT_PROVIDER_NOTES, text: supportNotes });
    }
    if (showCost) {
      const financeNotes = (
        getEffectivePricingField(row, pendingEdits, FINANCE_PRICING_NOTES) ?? ''
      ).trim();
      if (financeNotes) {
        lines.push({ key: FINANCE_PRICING_NOTES, text: `Finance: ${financeNotes}` });
      }
    }
    return lines;
  };

  const renderStackedCell = (row, primary, { showNotes = false } = {}) => {
    const notes = getProviderNoteLines(row);
    if (notes.length === 0) {
      return primary;
    }

    return (
      <div className="d-flex flex-column">
        <div>{primary}</div>
        {notes.map(note => (
          <div
            key={note.key}
            className="secondary-text u-margin-top--small"
            style={
              showNotes
                ? { fontSize: '0.85em', maxWidth: '18rem', overflowWrap: 'anywhere' }
                : { fontSize: '0.85em' }
            }>
            {showNotes ? note.text : '\u00a0'}
          </div>
        ))}
      </div>
    );
  };

  const renderProviderCell = row =>
    showInputs ? (
      <input
        type="text"
        className="form-control form-control-sm"
        value={getValue(row, 'provider')}
        onChange={e => handlePendingChange(row, 'provider', e, { trim: true })}
        disabled={saving}
        placeholder="Provider"
        aria-label={`Provider for ${row.title}`}
      />
    ) : (
      getValue(row, 'provider').trim() || '-'
    );

  const renderNotesCell = (row, field) => {
    const copy = NOTE_FIELDS[field];
    const notes = (getEffectivePricingField(row, pendingEdits, field) ?? '').trim();
    return (
      <div
        className="u-link dark"
        role="button"
        tabIndex={0}
        onClick={() => onOpenNotes(row, field)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpenNotes(row, field);
          }
        }}>
        {notes ? copy.viewLabel : copy.addLabel}
      </div>
    );
  };

  return (
    <>
      {filtered.map(row => {
        const marginMetrics = showMargin
          ? calculatePricingMarginInBaseCurrency(
              row,
              pendingEditsWithNumericDrafts(pendingEdits, numericDrafts, row, exchangeRate),
              {
                exchangeRate,
                internalCostManual,
                internalCostNonManual,
              },
            )
          : null;
        return (
          <Table.Row
            key={`pricing-${row.id ?? ''}-${row.is_additional_charge ? 'additional' : 'catalog'}`}>
            <Table.Col>{renderStackedCell(row, row.title ?? '-')}</Table.Col>
            <Table.Col>
              {renderStackedCell(row, row.charge_type_display ?? row.charge_type ?? '-')}
            </Table.Col>
            <Table.Col>{renderStackedCell(row, row.source_display ?? '-')}</Table.Col>
            <Table.Col>
              {renderStackedCell(row, renderProviderCell(row), { showNotes: true })}
            </Table.Col>
            <Table.Col>
              {renderStackedCell(
                row,
                showInputs ? (
                  <select
                    className="form-control form-control-sm"
                    value={getBaseCurrency(row, pendingEdits)}
                    onChange={e => handleBaseCurrencyChange(row, e)}
                    disabled={saving}
                    aria-label={`Base currency for ${row.title}`}>
                    {BASE_CURRENCY_OPTIONS.map(currency => (
                      <option key={currency} value={currency}>
                        {currency}
                      </option>
                    ))}
                  </select>
                ) : (
                  getBaseCurrency(row, pendingEdits)
                ),
              )}
            </Table.Col>
            {showCost && (
              <>
                <Table.Col divider>
                  {renderStackedCell(
                    row,
                    showInputs
                      ? renderNumericInput(row, 'provider_cost', {
                          onCommit: value => commitProviderCost(row, value),
                          ariaLabel: `Provider cost for ${row.title}`,
                        })
                      : formatPrice(getEffectivePricingField(row, pendingEdits, 'provider_cost')),
                  )}
                </Table.Col>
                <Table.Col>
                  {renderStackedCell(
                    row,
                    formatPrice(
                      getDisplayInternalCost(
                        getBaseCurrency(row, pendingEdits),
                        resolveInternalCostAud(row, internalCostManual, internalCostNonManual),
                        exchangeRate,
                      ),
                    ),
                  )}
                </Table.Col>
              </>
            )}
            <Table.Col divider>
              {renderStackedCell(row, renderPriceCell(row, 'price_aud'))}
            </Table.Col>
            <Table.Col>{renderStackedCell(row, renderPriceCell(row, 'price_nzd'))}</Table.Col>
            <Table.Col>{renderStackedCell(row, renderPriceCell(row, 'price_usd'))}</Table.Col>
            {showMargin && (
              <Table.Col>
                {renderStackedCell(row, formatMarginPctDisplay(marginMetrics.marginPct))}
              </Table.Col>
            )}
            <Table.Col divider>
              {renderStackedCell(
                row,
                isFixedPricingMode(row.pricing_mode)
                  ? formatPrice(
                      getAnchorPriceValue(
                        row,
                        pendingEditsWithNumericDrafts(
                          pendingEdits,
                          numericDrafts,
                          row,
                          exchangeRate,
                        ),
                      ),
                    )
                  : showInputs
                    ? renderNumericInput(row, 'floor', {
                        onCommit: value => commitFloor(row, value),
                        ariaLabel: `Floor for ${row.title}`,
                      })
                    : formatPrice(getEffectivePricingField(row, pendingEdits, 'floor')),
              )}
            </Table.Col>
            <Table.Col>{renderStackedCell(row, renderMaxDiscountCell(row))}</Table.Col>
            {showMargin && (
              <Table.Col>
                {renderStackedCell(row, formatMarginPctDisplay(marginMetrics.floorMarginPct))}
              </Table.Col>
            )}
            {showInputs && (
              <>
                <Table.Col divider>
                  {renderStackedCell(row, renderNotesCell(row, SUPPORT_PROVIDER_NOTES))}
                </Table.Col>
                <Table.Col>
                  {renderStackedCell(row, renderNotesCell(row, FINANCE_PRICING_NOTES))}
                </Table.Col>
              </>
            )}
          </Table.Row>
        );
      })}
    </>
  );
};

const CheckTypePricingTab = () => {
  const authentication = useSelector(selectAuthentication);
  const hasPermission = useAccessControl(authentication?.admin_permissions);
  const canEdit = hasPermission(AdminPermissions.UpdatePricing);
  const showCost = hasPermission(AdminPermissions.ListCost);
  const [editing, setEditing] = useState(false);
  const showInputs = canEdit && editing;

  const { data: checkGroups = [] } = useGetCheckGroupsQuery();
  const { data: allCheckTypes = [] } = useGetCheckTypesQuery();
  const { data: checkTypePricing = [], isLoading, refetch } = useGetCheckTypePricingQuery();
  const { data: pricingSettings } = useGetGlobalRatesQuery();
  const [updateCheckTypePricing] = useUpdateCheckTypePricingMutation();
  const [updateGlobalRates] = useUpdateGlobalRatesMutation();
  const [getCompanyChecksReport] = useLazyGetCompanyChecksReportQuery();
  const [saving, setSaving] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);
  const [pendingEdits, setPendingEdits] = useState({});
  const [numericDrafts, setNumericDrafts] = useState({});
  const [reportDownloading, setReportDownloading] = useState(false);
  const [exchangeRateInput, setExchangeRateInput] = useState('');
  const effectiveExchangeRate = useMemo(
    () => parseExchangeRate(exchangeRateInput),
    [exchangeRateInput],
  );
  const syncDerivedDebounceRef = useRef(null);
  const [internalCostManualInput, setInternalCostManualInput] = useState('');
  const [internalCostNonManualInput, setInternalCostNonManualInput] = useState('');
  const effectiveInternalCostManual = useMemo(
    () => parseNonNegativeNumber(internalCostManualInput),
    [internalCostManualInput],
  );
  const effectiveInternalCostNonManual = useMemo(
    () => parseNonNegativeNumber(internalCostNonManualInput),
    [internalCostNonManualInput],
  );
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [notesRow, setNotesRow] = useState(null);
  const [notesField, setNotesField] = useState(null);
  const [showNotesModal, setShowNotesModal] = useState(false);

  const savedViewsState = useSavedViews('admin_pricing');

  const handleSavedViewSelect = (id, params) => {
    savedViewsState.setActiveViewId(id);
    savedViewsState.setPendingApply({ params, key: Date.now() });
  };

  const activeSavedView = savedViewsState.savedViews.find(
    v => Number(v.id) === Number(savedViewsState.activeViewId),
  );
  const savedViewBanner = activeSavedView ? (
    <ActiveViewBanner
      viewName={activeSavedView.name}
      activeViewData={activeSavedView}
      bannerViewQuerySynced={savedViewsState.bannerViewQuerySynced}
      onClearView={() => {
        savedViewsState.setActiveViewId(null);
        savedViewsState.setPendingApply(null);
      }}
      onRestore={() => {}}
      onUpdate={savedViewsState.handleUpdate}
      onSaveNew={() => savedViewsState.setSaveViewModal(true)}
      onSetDefault={savedViewsState.handleSetDefault}
    />
  ) : null;

  useEffect(() => {
    if (!pricingSettings || settingsDirty) return;
    if (pricingSettings.exchange_rate != null) {
      setExchangeRateInput(String(pricingSettings.exchange_rate));
    }
    if (pricingSettings.internal_cost_manual != null) {
      setInternalCostManualInput(String(pricingSettings.internal_cost_manual));
    }
    if (pricingSettings.internal_cost_non_manual != null) {
      setInternalCostNonManualInput(String(pricingSettings.internal_cost_non_manual));
    }
  }, [pricingSettings, settingsDirty]);

  const checkTypePricingList = useMemo(
    () => (Array.isArray(checkTypePricing) ? checkTypePricing : []),
    [checkTypePricing],
  );

  const handleEditingChange = useCallback(
    checked => {
      if (!checked) {
        setPendingEdits(prev =>
          pruneMatchingPendingEdits(
            mergeNumericDraftsIntoPendingEdits(
              prev,
              numericDrafts,
              checkTypePricingList,
              effectiveExchangeRate,
            ),
            checkTypePricingList,
          ),
        );
        setNumericDrafts({});
      }
      setEditing(checked);
    },
    [numericDrafts, checkTypePricingList, effectiveExchangeRate],
  );

  const pendingTableEdits = useMemo(() => {
    const merged = mergeNumericDraftsIntoPendingEdits(
      pendingEdits,
      numericDrafts,
      checkTypePricingList,
      effectiveExchangeRate,
    );
    return pruneMatchingPendingEdits(merged, checkTypePricingList);
  }, [pendingEdits, numericDrafts, checkTypePricingList, effectiveExchangeRate]);

  const pendingTableRowCount = Object.keys(pendingTableEdits).length;

  const onPendingChange = useCallback((rowId, fieldOrUpdates, value) => {
    setPendingEdits(prev => {
      const updates =
        typeof fieldOrUpdates === 'object' && fieldOrUpdates !== null
          ? fieldOrUpdates
          : { [fieldOrUpdates]: value };
      return {
        ...prev,
        [rowId]: { ...(prev[rowId] ?? {}), ...updates },
      };
    });
  }, []);

  const handleOpenNotes = useCallback((row, field) => {
    setNotesRow(row);
    setNotesField(field);
    setShowNotesModal(true);
  }, []);

  const handleCloseNotes = useCallback(() => {
    setShowNotesModal(false);
    setNotesRow(null);
    setNotesField(null);
  }, []);

  const handleSaveNotes = useCallback(
    async notes => {
      if (!notesRow?.id || !notesField) return;
      setNotesSaving(true);
      try {
        await updateCheckTypePricing({
          id: Number(notesRow.id),
          [notesField]: notes,
        }).unwrap();
        setPendingEdits(prev => {
          const rowEdits = prev[notesRow.id];
          if (!rowEdits || !Object.prototype.hasOwnProperty.call(rowEdits, notesField)) {
            return prev;
          }
          const nextRowEdits = { ...rowEdits };
          delete nextRowEdits[notesField];
          if (Object.keys(nextRowEdits).length === 0) {
            const next = { ...prev };
            delete next[notesRow.id];
            return next;
          }
          return { ...prev, [notesRow.id]: nextRowEdits };
        });
        setShowNotesModal(false);
        setNotesRow(null);
        setNotesField(null);
      } catch (err) {
        console.error('Save pricing notes:', err?.data?.message || err?.message, err);
      } finally {
        setNotesSaving(false);
      }
    },
    [notesRow, notesField, updateCheckTypePricing],
  );

  const handleSave = useCallback(() => {
    const editsToSave = pruneMatchingPendingEdits(
      mergeNumericDraftsIntoPendingEdits(
        pendingEdits,
        numericDrafts,
        checkTypePricingList,
        effectiveExchangeRate,
      ),
      checkTypePricingList,
    );
    const ids = Object.keys(editsToSave);
    if (ids.length === 0) {
      return;
    }
    setNumericDrafts({});
    setPendingEdits(editsToSave);
    setSaving(true);
    const updates = ids.map(id =>
      updateCheckTypePricing({ id: Number(id), ...editsToSave[id] }).unwrap(),
    );
    Promise.all(updates)
      .then(() => {
        setPendingEdits({});
        return refetch();
      })
      .catch(err => {
        console.error('Save check type pricing:', err?.data?.message || err?.message, err);
      })
      .finally(() => setSaving(false));
  }, [
    pendingEdits,
    numericDrafts,
    checkTypePricingList,
    effectiveExchangeRate,
    updateCheckTypePricing,
    refetch,
  ]);

  const syncDerivedPricesToPendingEdits = useCallback(
    rate => {
      setPendingEdits(prev => {
        const next = { ...prev };
        checkTypePricingList.forEach(row => {
          const rowEdits = { ...(next[row.id] ?? {}) };
          const pending = { ...next, [row.id]: rowEdits };
          if (getAnchorPriceValue(row, pending) == null) {
            return;
          }
          const derived = getDerivedPricesForRow(row, pending, rate);
          ['price_aud', 'price_nzd', 'price_usd'].forEach(field => {
            const derivedVal = derived[field];
            if (!pricingFieldValuesEqual(row[field], derivedVal)) {
              rowEdits[field] = derivedVal;
            } else if (field in rowEdits) {
              delete rowEdits[field];
            }
          });
          const pruned = {};
          Object.entries(rowEdits).forEach(([field, value]) => {
            if (!pricingFieldValuesEqual(row[field], value)) {
              pruned[field] = value;
            }
          });
          if (Object.keys(pruned).length === 0) {
            delete next[row.id];
          } else {
            next[row.id] = pruned;
          }
        });
        return next;
      });
    },
    [checkTypePricingList],
  );

  const scheduleSyncDerivedPrices = useCallback(
    rate => {
      if (syncDerivedDebounceRef.current) {
        clearTimeout(syncDerivedDebounceRef.current);
      }
      syncDerivedDebounceRef.current = setTimeout(() => {
        syncDerivedPricesToPendingEdits(rate);
      }, 300);
    },
    [syncDerivedPricesToPendingEdits],
  );

  useEffect(
    () => () => {
      if (syncDerivedDebounceRef.current) {
        clearTimeout(syncDerivedDebounceRef.current);
      }
    },
    [],
  );

  const handleSaveSettings = useCallback(() => {
    setSaving(true);
    updateGlobalRates({
      exchange_rate: effectiveExchangeRate,
      internal_cost_manual: effectiveInternalCostManual,
      internal_cost_non_manual: effectiveInternalCostNonManual,
    })
      .unwrap()
      .then(() => {
        setSettingsDirty(false);
        setExchangeRateInput(String(effectiveExchangeRate));
        setInternalCostManualInput(String(effectiveInternalCostManual));
        setInternalCostNonManualInput(String(effectiveInternalCostNonManual));
        return refetch();
      })
      .catch(err => {
        console.error('Save pricing settings:', err?.data?.message || err?.message, err);
      })
      .finally(() => setSaving(false));
  }, [
    updateGlobalRates,
    effectiveExchangeRate,
    effectiveInternalCostManual,
    effectiveInternalCostNonManual,
    refetch,
  ]);

  const checkGroupOptions = [
    { name: 'All Groups', value: 'all' },
    ...checkGroups.map(opt => ({ name: opt.name ?? opt.value, value: opt.value })),
  ];

  const checkTypeOptions = useMemo(
    () =>
      buildCheckTypeFilterOptions(allCheckTypes, {
        includeSupportChecks: true,
        excludeDeprecated: true,
      }),
    [allCheckTypes],
  );

  const priceNameOptions = useMemo(() => {
    const byCode = new Map();
    checkTypePricingList.forEach(r => {
      if (r.price_name !== 'country.name' || !r.source_value) return;
      if (!byCode.has(r.source_value)) {
        byCode.set(r.source_value, {
          name: r.source_display ?? r.source_value,
          value: r.source_value,
        });
      }
    });
    const countries = [...byCode.values()].sort((a, b) => a.name.localeCompare(b.name));
    return countries;
  }, [checkTypePricingList]);

  const defaultQuery = {
    page: { value: 1 },
    check_group: {
      value: ['all'],
      name: ['All Groups'],
      type: 'status',
      title: 'Check Group',
    },
    check_type: {
      value: [ALL_TYPES_FILTER_VALUE],
      name: [ALL_TYPES_FILTER_NAME],
      type: 'status',
      title: 'Check Type',
    },
    country: {
      value: ['all'],
      name: ['All'],
      type: 'status',
      title: 'Countries Available',
    },
    price_name: {
      value: [],
      name: [],
      type: 'status',
      title: 'Subcheck Countries',
    },
    has_subchecks: {
      value: 'all',
      name: 'All',
      title: 'Subchecks',
    },
    company_active: {
      value: 'all',
      name: 'All Companies',
      title: 'Company Status',
    },
  };

  const [tableQuery, setTableQuery] = useState(defaultQuery);

  const tableRows = useMemo(() => {
    if (!checkTypePricingList.length) return [];
    const q = tableQuery || {};
    const getVal = key => getTableQueryValues(q, key);
    const checkGroupsFilter = getVal('check_group').filter(x => x !== 'all');
    const checkTypesFilter = getVal('check_type').filter(
      x => x !== 'all' && x !== SUPPORT_CHECKS_FILTER_VALUE,
    );
    const supportOnly = getVal('check_type').includes(SUPPORT_CHECKS_FILTER_VALUE);
    const countries = getVal('country').filter(x => x !== 'all');
    const priceNames = getVal('price_name').filter(x => x !== 'all');
    const hasSubchecks = q.has_subchecks?.value ?? q.has_subchecks ?? 'all';

    let rows = checkTypePricingList.map(r => ({
      ...r,
      check_group_title: r.check_group_title ?? r.check_group ?? '-',
    }));

    if (checkGroupsFilter.length > 0)
      rows = rows.filter(r => checkGroupsFilter.includes(r.check_group));
    if (supportOnly) rows = rows.filter(r => isTruthyDbBool(r.is_manual));
    if (checkTypesFilter.length > 0) rows = rows.filter(r => checkTypesFilter.includes(r.type));
    if (countries.length > 0) {
      rows = rows.filter(r => {
        const ca = r.countries_available;
        const arr = Array.isArray(ca)
          ? ca
          : typeof ca === 'string'
            ? (() => {
                try {
                  return JSON.parse(ca || '[]') ?? [];
                } catch {
                  return [];
                }
              })()
            : [];
        return countries.some(c => arr.includes(c));
      });
    }
    if (priceNames.length > 0) {
      rows = rows.filter(r => priceNames.includes(r.source_value));
    }
    if (hasSubchecks === 'yes') rows = rows.filter(r => !!r.has_subchecks);
    else if (hasSubchecks === 'no') rows = rows.filter(r => !r.has_subchecks);

    rows.sort((a, b) => {
      const ta = (a.title ?? '').localeCompare(b.title ?? '');
      if (ta !== 0) return ta;
      const aa = a.is_additional_charge ? 1 : 0;
      const ab = b.is_additional_charge ? 1 : 0;
      if (aa !== ab) return aa - ab;
      const da = a.is_default ? 1 : 0;
      const db = b.is_default ? 1 : 0;
      if (db !== da) return db - da;
      return adminListSourceSortKey(a).localeCompare(adminListSourceSortKey(b));
    });
    return rows;
  }, [checkTypePricingList, tableQuery]);

  const handleDownloadCsv = useCallback(() => {
    const escape = v => {
      const s = v == null ? '' : String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };
    const boolYesNo = v => (v ? 'Yes' : 'No');
    const headers = [
      'Check Type Title',
      'Check Group',
      'Type',
      'Charge Type',
      'Source',
      'Source Value',
      'Default',
      'Provider',
      'Support notes',
      'Base Currency',
      ...(showCost ? ['Provider Cost', 'Internal Cost'] : []),
      'AUD',
      'NZD',
      'USD',
      ...(showCost ? ['Margin', 'Margin (%)'] : []),
      'Floor',
      'Max discount (%)',
      ...(showCost ? ['Floor margin', 'Floor margin (%)', 'Total Cost'] : []),
      'Finance notes',
      'Countries Available',
      'Has Subchecks',
      'Pricing Mode',
      'Updated At',
      'Updated By',
    ];
    const csvRows = [
      headers.join(','),
      ...tableRows.map(r => {
        const countriesList = Array.isArray(r.countries_available)
          ? r.countries_available.join(', ')
          : typeof r.countries_available === 'string'
            ? r.countries_available
            : '';
        const sourceDisplay = r.source_display ?? '-';
        const chargeTypeDisplay = r.charge_type_display ?? r.charge_type ?? '-';
        const marginMetrics = showCost
          ? calculatePricingMarginInBaseCurrency(r, pendingEdits, {
              exchangeRate: effectiveExchangeRate,
              internalCostManual: effectiveInternalCostManual,
              internalCostNonManual: effectiveInternalCostNonManual,
            })
          : null;
        const derivedPrices = getDerivedPricesForRow(r, pendingEdits, effectiveExchangeRate);
        const maxDiscountPct = maxDiscountPctFromFloor(
          getAnchorPriceValue(r, pendingEdits),
          resolveFloorPrice(r, pendingEdits),
        );
        const rowCells = [
          escape(r.title ?? ''),
          escape(r.check_group_title ?? r.check_group ?? ''),
          escape(r.type ?? ''),
          escape(chargeTypeDisplay),
          escape(sourceDisplay),
          escape(r.source_value ?? ''),
          boolYesNo(r.is_default),
          escape(r.provider ?? ''),
          escape(getEffectivePricingField(r, pendingEdits, SUPPORT_PROVIDER_NOTES) ?? ''),
          escape(getBaseCurrency(r, pendingEdits)),
          ...(showCost
            ? [
                escape(getEffectivePricingField(r, pendingEdits, 'provider_cost') ?? ''),
                escape(
                  formatPricingAmount(
                    getDisplayInternalCost(
                      getBaseCurrency(r, pendingEdits),
                      resolveInternalCostAud(
                        r,
                        effectiveInternalCostManual,
                        effectiveInternalCostNonManual,
                      ),
                      effectiveExchangeRate,
                    ),
                  ),
                ),
              ]
            : []),
          escape(derivedPrices.price_aud ?? ''),
          escape(derivedPrices.price_nzd ?? ''),
          escape(derivedPrices.price_usd ?? ''),
          ...(showCost
            ? [
                escape(formatPricingAmount(marginMetrics.margin)),
                escape(
                  marginMetrics.marginPct != null && !Number.isNaN(marginMetrics.marginPct)
                    ? marginMetrics.marginPct.toFixed(1)
                    : '',
                ),
              ]
            : []),
          escape(
            isFixedPricingMode(r.pricing_mode)
              ? (getAnchorPriceValue(r, pendingEdits) ?? '')
              : (getEffectivePricingField(r, pendingEdits, 'floor') ?? ''),
          ),
          escape(
            maxDiscountPct != null && !Number.isNaN(maxDiscountPct)
              ? maxDiscountPct.toFixed(1)
              : '',
          ),
          ...(showCost
            ? [
                escape(formatPricingAmount(marginMetrics.floorMargin)),
                escape(
                  marginMetrics.floorMarginPct != null &&
                    !Number.isNaN(marginMetrics.floorMarginPct)
                    ? marginMetrics.floorMarginPct.toFixed(1)
                    : '',
                ),
                escape(formatPricingAmount(marginMetrics.totalCost)),
              ]
            : []),
          escape(getEffectivePricingField(r, pendingEdits, FINANCE_PRICING_NOTES) ?? ''),
          escape(countriesList),
          boolYesNo(r.has_subchecks),
          escape(r.pricing_mode ?? ''),
          escape(r.updated_at ?? ''),
          escape((r.updated_by_name ?? '').trim() || (r.updated_by_email ?? '')),
        ];
        return rowCells.join(',');
      }),
    ];
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'check_type_pricing_export.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [
    tableRows,
    showCost,
    canEdit,
    pendingEdits,
    effectiveExchangeRate,
    effectiveInternalCostManual,
    effectiveInternalCostNonManual,
  ]);

  const handleDownloadCompanyChecksPricing = useCallback(() => {
    setReportDownloading(true);
    getCompanyChecksReport(buildCompanyChecksReportParams(tableQuery))
      .unwrap()
      .finally(() => setReportDownloading(false));
  }, [getCompanyChecksReport, tableQuery]);

  return (
    <Table.Context
      defaultQuery={defaultQuery}
      manualQueryUpdate
      suppressQueryParamEffect={!savedViewsState.savedViewsReady}
      onQueryUpdate={setTableQuery}
      multiSelectNormalize={{
        check_group: { allValue: 'all', allName: 'All Groups' },
        check_type: ADMIN_CHECK_TYPE_MULTI_SELECT_NORMALIZE,
        country: { allValue: 'all', allName: 'All' },
      }}>
      <ReportingSavedViewsTableSync tab="admin_pricing" savedViewsState={savedViewsState} />
      {savedViewsState.savedViews.length > 0 && (
        <div className="d-flex justify-content-end u-margin--large u-margin-bottom--small">
          <SavedViewsDropdown
            savedViews={savedViewsState.savedViews}
            activeViewId={savedViewsState.activeViewId}
            onSelectView={handleSavedViewSelect}
            onSetDefault={savedViewsState.handleSetDefault}
            onManage={() => savedViewsState.setManageViewsOpen(true)}
            tab="admin_pricing"
          />
        </div>
      )}
      <Table.FloatingFilters
        className="u-margin--large surface-white"
        banner={savedViewBanner}
        actions={
          <Button
            type="secondary"
            icon="bookmark"
            onClick={() => savedViewsState.setSaveViewModal(true)}>
            Save View
          </Button>
        }>
        <Table.FloatingFiltersLeft className="u-gap-medium align-items-end">
          <Table.MultiSelectFilter
            title="Check Group"
            filterName="check_group"
            type="status"
            options={checkGroupOptions}
            useDefault={false}
            useSearchInMultiSelect
          />
          <Table.MultiSelectFilter
            title="Check Type"
            filterName="check_type"
            type="status"
            options={checkTypeOptions}
            useSearchInMultiSelect
          />
          <Table.MultiSelectFilter
            title="Countries Available"
            filterName="country"
            type="status"
            options={COUNTRY_OPTIONS}
            useDefault={false}
            useSearchInMultiSelect
          />
          <Table.MultiSelectFilter
            title="Subcheck Countries"
            filterName="price_name"
            type="status"
            options={priceNameOptions}
            useDefault={false}
            useSearchInMultiSelect
          />
          <Table.Filter
            title="Subchecks"
            filterName="has_subchecks"
            options={HAS_SUBCHECKS_OPTIONS}
            useDefault={false}
          />
          {canEdit && (
            <Table.Filter
              title="Company Status"
              filterName="company_active"
              options={COMPANY_ACTIVE_OPTIONS}
              useDefault={false}
            />
          )}
        </Table.FloatingFiltersLeft>
        <Table.FloatingFiltersRight>
          <ApplyFiltersButton />
        </Table.FloatingFiltersRight>
      </Table.FloatingFilters>
      <Table.TableContent className="u-margin--large">
        <div
          className="u-padding-x--large u-padding-top"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: '1rem',
          }}>
          <div
            className="u-gap-medium"
            style={{
              display: 'flex',
              flexDirection: 'column',
              flex: '0 0 33%',
              maxWidth: '33%',
              minWidth: '14rem',
            }}>
            <div style={{ width: '100%' }}>
              <Table.Search placeholder="Search pricing..." />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
              {canEdit && (
                <>
                  <Button icon="download" onClick={handleDownloadCsv}>
                    Download CSV
                  </Button>
                  <Button
                    icon="download"
                    onClick={handleDownloadCompanyChecksPricing}
                    disabled={reportDownloading}>
                    {reportDownloading ? 'Downloading…' : 'Company Checks Pricing Report'}
                  </Button>
                </>
              )}
            </div>
          </div>
          {canEdit && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: '0.75rem',
                flex: '1 1 20rem',
                minWidth: 0,
                marginLeft: 'auto',
              }}>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: '0.75rem 1rem',
                }}>
                <label
                  className="secondary-text u-gap-small"
                  style={{ display: 'flex', alignItems: 'center', marginBottom: 0 }}>
                  <span style={{ whiteSpace: 'nowrap' }}>Edit</span>
                  <Toggle
                    small
                    inputProps={{
                      checked: editing,
                      onChange: e => handleEditingChange(e.target.checked),
                      'aria-label': 'Edit pricing values',
                    }}
                  />
                </label>
                <label
                  className="secondary-text u-gap-small"
                  style={{ display: 'flex', alignItems: 'center', marginBottom: 0 }}>
                  <span style={{ whiteSpace: 'nowrap' }}>Exchange rate (AUD/NZD → USD)</span>
                  {showInputs ? (
                    <input
                      type="number"
                      step="any"
                      min="0.01"
                      className="form-control form-control-sm"
                      style={{ width: '7.5rem' }}
                      value={exchangeRateInput}
                      onChange={e => {
                        const raw = e.target.value;
                        setExchangeRateInput(raw);
                        setSettingsDirty(true);
                        const rate = parseExchangeRate(raw, null);
                        if (rate != null) {
                          scheduleSyncDerivedPrices(rate);
                        }
                      }}
                      onBlur={() => {
                        const rate = parseExchangeRate(
                          exchangeRateInput,
                          pricingSettings?.exchange_rate,
                        );
                        setExchangeRateInput(rate != null ? String(rate) : '');
                        if (settingsDirty && rate != null) {
                          scheduleSyncDerivedPrices(rate);
                        }
                      }}
                      disabled={saving}
                      aria-label="Exchange rate AUD/NZD to USD"
                    />
                  ) : (
                    <span>{exchangeRateInput || '-'}</span>
                  )}
                </label>
                <label
                  className="secondary-text u-gap-small"
                  style={{ display: 'flex', alignItems: 'center', marginBottom: 0 }}>
                  <span style={{ whiteSpace: 'nowrap' }}>Internal cost — manual (AUD)</span>
                  {showInputs ? (
                    <input
                      type="number"
                      step="any"
                      min="0"
                      className="form-control form-control-sm"
                      style={{ width: '7.5rem' }}
                      value={internalCostManualInput}
                      onChange={e => {
                        setInternalCostManualInput(e.target.value);
                        setSettingsDirty(true);
                      }}
                      onBlur={() => {
                        setInternalCostManualInput(
                          normalizeNumericInputOnBlur(internalCostManualInput, {
                            fallback: effectiveInternalCostManual,
                            min: 0,
                            decimals: 2,
                          }),
                        );
                      }}
                      disabled={saving}
                      aria-label="Internal cost AUD for manual checks"
                    />
                  ) : (
                    <span>{internalCostManualInput || '-'}</span>
                  )}
                </label>
                <label
                  className="secondary-text u-gap-small"
                  style={{ display: 'flex', alignItems: 'center', marginBottom: 0 }}>
                  <span style={{ whiteSpace: 'nowrap' }}>Internal cost — non-manual (AUD)</span>
                  {showInputs ? (
                    <input
                      type="number"
                      step="any"
                      min="0"
                      className="form-control form-control-sm"
                      style={{ width: '7.5rem' }}
                      value={internalCostNonManualInput}
                      onChange={e => {
                        setInternalCostNonManualInput(e.target.value);
                        setSettingsDirty(true);
                      }}
                      onBlur={() => {
                        setInternalCostNonManualInput(
                          normalizeNumericInputOnBlur(internalCostNonManualInput, {
                            fallback: effectiveInternalCostNonManual,
                            min: 0,
                            decimals: 2,
                          }),
                        );
                      }}
                      disabled={saving}
                      aria-label="Internal cost AUD for non-manual checks"
                    />
                  ) : (
                    <span>{internalCostNonManualInput || '-'}</span>
                  )}
                </label>
                {showInputs && (
                  <Button
                    type="primary"
                    icon="save"
                    onClick={handleSaveSettings}
                    disabled={saving || !settingsDirty}>
                    Save rates
                  </Button>
                )}
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: '0.75rem',
                }}>
                {pendingTableRowCount > 0 && (
                  <span className="secondary-text" style={{ fontSize: '0.9em' }}>
                    {pendingTableRowCount} unsaved row{pendingTableRowCount === 1 ? '' : 's'}
                  </span>
                )}
                {(showInputs || pendingTableRowCount > 0) && (
                  <Button
                    type="primary"
                    icon="save"
                    onClick={handleSave}
                    disabled={saving || pendingTableRowCount === 0}>
                    {pendingTableRowCount > 0
                      ? `Save table changes (${pendingTableRowCount})`
                      : 'Save table changes'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
        {canEdit && pendingTableRowCount > 0 && (
          <div className="u-padding-x--large">
            <FormMessage
              type={MessageType.Info}
              closeable={false}
              message={`You have unsaved table changes (${pendingTableRowCount} row${pendingTableRowCount === 1 ? '' : 's'}). Click Save table changes to persist them.`}
            />
          </div>
        )}
        <div className="u-margin--large">
          <Table>
            <Table.Head sticky>
              <Table.Row key="header-row">
                <Table.Header key="title">Title</Table.Header>
                <Table.Header key="charge_type">Charge Type</Table.Header>
                <Table.Header key="source_value">Source Value</Table.Header>
                <Table.Header key="provider">Provider</Table.Header>
                <Table.Header key="base_currency">Base currency</Table.Header>
                {showCost && (
                  <>
                    <Table.Header key="provider_cost" divider>
                      Provider Cost
                    </Table.Header>
                    <Table.Header key="internal_cost">Internal Cost</Table.Header>
                  </>
                )}
                <Table.Header key="price_aud" divider>
                  AUD
                </Table.Header>
                <Table.Header key="price_nzd">NZD</Table.Header>
                <Table.Header key="price_usd">USD</Table.Header>
                {showCost && <Table.Header key="margin">Margin</Table.Header>}
                <Table.Header key="floor" divider>
                  Floor
                </Table.Header>
                <Table.Header key="max_discount">Max discount</Table.Header>
                {showCost && <Table.Header key="floor_margin">Floor margin</Table.Header>}
                {showInputs && (
                  <>
                    <Table.Header key={SUPPORT_PROVIDER_NOTES} divider>
                      {NOTE_FIELDS[SUPPORT_PROVIDER_NOTES].column}
                    </Table.Header>
                    <Table.Header key={FINANCE_PRICING_NOTES}>
                      {NOTE_FIELDS[FINANCE_PRICING_NOTES].column}
                    </Table.Header>
                  </>
                )}
              </Table.Row>
            </Table.Head>
            <Table.Body>
              <PricingTableBody
                tableRows={tableRows}
                canEdit={canEdit}
                showInputs={showInputs}
                showCost={showCost}
                showMargin={showCost}
                pendingEdits={pendingEdits}
                numericDrafts={numericDrafts}
                setNumericDrafts={setNumericDrafts}
                onPendingChange={onPendingChange}
                onOpenNotes={handleOpenNotes}
                saving={saving}
                exchangeRate={effectiveExchangeRate}
                internalCostManual={effectiveInternalCostManual}
                internalCostNonManual={effectiveInternalCostNonManual}
              />
            </Table.Body>
          </Table>
        </div>
        {notesRow && notesField && (
          <PricingNotesModal
            visible={showNotesModal}
            row={notesRow}
            field={notesField}
            notes={getEffectivePricingField(notesRow, pendingEdits, notesField)}
            onClose={handleCloseNotes}
            onSave={handleSaveNotes}
            saving={notesSaving}
          />
        )}
        {tableRows.length === 0 && !isLoading && (
          <p className="secondary-text u-padding--large">No pricing records match the filters.</p>
        )}
      </Table.TableContent>
    </Table.Context>
  );
};

const AdminPricing = () => {
  useDocumentTitle('Pricing');

  return (
    <AdminPage title="Pricing" allowedRole={AdminPermissions.ListPricing}>
      <CheckTypePricingTab />
    </AdminPage>
  );
};

export default AdminPricing;
